/**
 * React Query hook for Calendar Attendees
 * Provides automatic caching, background sync, and optimistic updates
 */

import { useQuery } from '@tanstack/react-query';
import { getCalendarAttendees } from 'services/users/userServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Fetches calendar attendees for a specific case or global context
 *
 * @param {Object} params - Query parameters
 * @param {string} params.caseId - Case ID (optional for global)
 * @param {boolean} params.all_contacts - Include all contacts
 * @param {boolean} params.is_lead - Is lead context
 * @param {boolean} params.is_active - Include only active users
 * @param {boolean} params.active_groups - Include only active groups
 * @returns {Promise<Array>} - Array of calendar attendees
 */
const fetchCalendarAttendees = async (params = {}) => {
  const response = await getCalendarAttendees(params);
  return response.data || [];
};

/**
 * Hook to fetch calendar attendees with React Query
 *
 * @param {Object} options - Configuration options
 * @param {string} options.caseId - Case ID (optional)
 * @param {boolean} options.all_contacts - Include all contacts (default: false)
 * @param {boolean} options.is_lead - Is lead context (default: false)
 * @param {boolean} options.is_active - Include only active users (default: true)
 * @param {boolean} options.active_groups - Include only active groups (default: false)
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - attendeesList, isLoading, error, refetch, isSuccess
 *
 * @example
 * // Fetch attendees for a case
 * const { attendeesList, isLoading } = useCalendarAttendeesQuery({
 *   caseId: 'case123',
 *   all_contacts: true,
 *   is_active: true
 * });
 *
 * @example
 * // Fetch global attendees with active groups
 * const { attendeesList } = useCalendarAttendeesQuery({
 *   all_contacts: true,
 *   is_active: true,
 *   active_groups: true
 * });
 */
export const useCalendarAttendeesQuery = ({
  caseId,
  all_contacts = false,
  is_lead = false,
  is_active = true,
  active_groups = false,
  enabled = true,
  excludeFromLoading = false,
} = {}) => {
  // Create a stable query key based on parameters
  const queryKey = [
    ...queryKeys.users.all,
    'calendar-attendees',
    caseId || 'global',
    {
      all_contacts,
      is_lead,
      is_active,
      active_groups,
    },
  ];

  const { data, isLoading, isFetching, error, refetch, isSuccess } = useQuery({
    queryKey,
    queryFn: () =>
      fetchCalendarAttendees({
        caseId,
        all_contacts,
        is_lead,
        is_active,
        active_groups,
        excludeFromLoading,
      }),
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 2,
    enabled, // Allow disabling the query
  });

  return {
    attendeesList: data || [],
    isLoading,
    isFetching,
    error,
    refetch,
    isSuccess,
  };
};
